import { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link, useRouter } from '@tanstack/react-router'
import { App, Button, Form, Input } from 'antd'
import { createSchemaFieldRule } from 'antd-zod'
import { ArrowRight, BadgeCheck, LockKeyhole, Mail, Phone, UserRound } from 'lucide-react'
import { signUpSchema, type SignUpInput } from '@super-mega/server/contracts/auth'
import {
  AUTH_FIELD_CLASS_NAME,
  AUTH_FORM_CLASS_NAMES,
  AUTH_PAIRED_FIELD_CLASS_NAME,
  AuthFormHeader,
  AuthShell,
  getAuthError
} from '@/features/auth/index.ts'
import { useTRPC } from '@/trpc/react.ts'

const signUpRule = createSchemaFieldRule(signUpSchema)
const signUpErrorToastKey = 'auth-signup-error'
const verificationToastKey = 'auth-signup-verification'

export const Route = createFileRoute('/auth/signup')({
  component: SignUpPage,
  head: () => ({
    meta: [{ title: '注册 · Super Mega 租赁管理一体化智能系统' }]
  })
})

function SignUpPage() {
  const { redirect: redirectAfterSignUp } = Route.useSearch()
  const trpc = useTRPC()
  const router = useRouter()
  const queryClient = useQueryClient()
  const { message: messageApi } = App.useApp()
  const [form] = Form.useForm<SignUpInput>()
  const [retryAfterSeconds, setRetryAfterSeconds] = useState(0)

  const signUp = useMutation(trpc.auth.signUp.mutationOptions())
  const sendVerification = useMutation(trpc.auth.sendSignUpVerification.mutationOptions())

  useEffect(() => {
    if (retryAfterSeconds <= 0) {
      return
    }

    const timer = window.setTimeout(() => {
      setRetryAfterSeconds((seconds) => Math.max(0, seconds - 1))
    }, 1_000)
    return () => window.clearTimeout(timer)
  }, [retryAfterSeconds])

  const handleSendVerification = async () => {
    let email: string
    try {
      const values = await form.validateFields(['email'])
      email = values.email
    } catch {
      return
    }

    messageApi.destroy(verificationToastKey)
    try {
      const result = await sendVerification.mutateAsync({ email })
      setRetryAfterSeconds(result.retryAfterSeconds)
      void messageApi.open({
        content: '验证码已发送，请检查邮箱',
        duration: 3,
        key: verificationToastKey,
        type: 'success'
      })
    } catch (mutationError) {
      const presentation = getAuthError(mutationError, '发送验证码')
      void messageApi.open({
        content: presentation.message,
        duration: 3,
        key: verificationToastKey,
        type: 'error'
      })
    }
  }

  const handleSubmit = async (values: SignUpInput) => {
    messageApi.destroy(signUpErrorToastKey)

    try {
      const session = await signUp.mutateAsync(values)
      queryClient.setQueryData(trpc.auth.session.queryKey(), session)
      await router.navigate({ to: '/auth/status', replace: true })
    } catch (mutationError) {
      const presentation = getAuthError(mutationError, '注册')
      void messageApi.open({
        content: presentation.message,
        duration: 3,
        key: signUpErrorToastKey,
        type: 'error'
      })
    }
  }

  return (
    <AuthShell>
      <AuthFormHeader description="验证邮箱并提交申请，审核通过即可使用。" title="创建账号" />

      <Form<SignUpInput>
        classNames={AUTH_FORM_CLASS_NAMES}
        disabled={signUp.isPending || sendVerification.isPending}
        form={form}
        initialValues={{
          confirmPassword: '',
          email: '',
          name: '',
          password: '',
          verificationCode: ''
        }}
        layout="vertical"
        onFinish={(values) => void handleSubmit(values)}
        requiredMark={false}
        scrollToFirstError={{ block: 'nearest', scrollMode: 'if-needed', focus: true }}
        size="large"
      >
        <div className="grid grid-cols-2 gap-x-3 sm:gap-x-4">
          <Form.Item<SignUpInput>
            className={AUTH_PAIRED_FIELD_CLASS_NAME}
            label="姓名"
            name="name"
            rules={[signUpRule]}
          >
            <Input
              autoComplete="name"
              autoFocus
              maxLength={80}
              placeholder="请输入姓名"
              prefix={<UserRound aria-hidden className="size-4" strokeWidth={1.8} />}
            />
          </Form.Item>

          <Form.Item<SignUpInput>
            className={AUTH_PAIRED_FIELD_CLASS_NAME}
            label="手机（选填）"
            name="phone"
            rules={[signUpRule]}
          >
            <Input
              autoComplete="tel"
              maxLength={30}
              placeholder="请输入手机号"
              prefix={<Phone aria-hidden className="size-4" strokeWidth={1.8} />}
              type="tel"
            />
          </Form.Item>
        </div>

        <Form.Item<SignUpInput> className={AUTH_FIELD_CLASS_NAME} label="邮箱" name="email" rules={[signUpRule]}>
          <Input
            autoComplete="email"
            autoCapitalize="none"
            maxLength={255}
            placeholder="name@example.com"
            prefix={<Mail aria-hidden className="size-4" strokeWidth={1.8} />}
            spellCheck={false}
          />
        </Form.Item>

        <Form.Item<SignUpInput>
          className={AUTH_FIELD_CLASS_NAME}
          label="邮箱验证码"
          name="verificationCode"
          rules={[signUpRule]}
        >
          <Input
            autoCapitalize="none"
            autoComplete="one-time-code"
            maxLength={6}
            placeholder="6 位验证码"
            prefix={<BadgeCheck aria-hidden className="size-4" strokeWidth={1.8} />}
            spellCheck={false}
            suffix={
              <span className="border-border inline-flex items-center border-l pl-2">
                <Button
                  className="px-1! text-xs!"
                  disabled={retryAfterSeconds > 0 || signUp.isPending}
                  htmlType="button"
                  loading={sendVerification.isPending}
                  onClick={() => void handleSendVerification()}
                  size="small"
                  type="link"
                >
                  {retryAfterSeconds > 0 ? `${retryAfterSeconds}s 后重发` : '获取验证码'}
                </Button>
              </span>
            }
          />
        </Form.Item>

        <div className="grid grid-cols-2 gap-x-3 sm:gap-x-4">
          <Form.Item<SignUpInput>
            className={AUTH_PAIRED_FIELD_CLASS_NAME}
            label="密码"
            name="password"
            rules={[signUpRule]}
          >
            <Input.Password
              autoComplete="new-password"
              maxLength={1024}
              placeholder="至少 8 位"
              prefix={<LockKeyhole aria-hidden className="size-4" strokeWidth={1.8} />}
            />
          </Form.Item>

          <Form.Item<SignUpInput>
            className={AUTH_PAIRED_FIELD_CLASS_NAME}
            dependencies={['password']}
            label="确认密码"
            name="confirmPassword"
            rules={[signUpRule]}
          >
            <Input.Password
              autoComplete="new-password"
              maxLength={1024}
              placeholder="再次输入密码"
              prefix={<LockKeyhole aria-hidden className="size-4" strokeWidth={1.8} />}
            />
          </Form.Item>
        </div>

        <Button
          block
          className="h-12! font-semibold!"
          htmlType="submit"
          icon={<ArrowRight aria-hidden className="size-4" />}
          iconPlacement="end"
          loading={signUp.isPending}
          type="primary"
        >
          提交账号申请
        </Button>
      </Form>

      <div className="text-content-muted mt-4 text-center text-sm leading-5">
        已有账号？
        <Link
          className="ml-1 font-medium !text-[var(--primary)] hover:underline"
          search={{ redirect: redirectAfterSignUp }}
          to="/auth/signin"
        >
          返回登录
        </Link>
      </div>
    </AuthShell>
  )
}
